// packages/contracts/src/enums.ts
var USER_ROLES = ["owner", "dispatcher", "accountant", "driver", "customer", "mechanic"];
var ORDER_STATUSES = [
  "draft",
  "confirmed",
  "planned",
  "assigned",
  "loaded",
  "in_progress",
  "delivered",
  "documents_pending",
  "ready_to_invoice",
  "invoiced",
  "paid",
  "closed",
  "cancelled"
];
var TRIP_STATUSES = [
  "draft",
  "planned",
  "dispatched",
  "accepted",
  "in_progress",
  "completed",
  "cancelled"
];
var STOP_KINDS = ["pickup", "delivery"];
var STOP_STATUSES = ["pending", "en_route", "arrived", "completed", "failed"];
var LEG_KINDS = ["loaded", "empty", "repositioning", "return_home"];
var DOCUMENT_TYPES = [
  "cmr",
  "pod",
  "delivery_note",
  "packing_list",
  "invoice",
  "purchase_invoice",
  "fuel_receipt",
  "toll_receipt",
  "ferry_ticket",
  "weighbridge",
  "photo_loading",
  "photo_damage",
  "photo_seal",
  "customs",
  "insurance",
  "vehicle_document",
  "driver_document",
  "other",
  "transport_order",
  "odometer",
  "vehicle_registration",
  "id_card",
  "driving_licence",
  "medical_certificate",
  "criminal_record",
  "bank_details",
  "certificate",
  "posting_certificate",
  "vignette"
];
var DRIVER_PERSONAL_DOCUMENT_TYPES = [
  "id_card",
  "driving_licence",
  "medical_certificate",
  "criminal_record",
  "bank_details",
  "certificate",
  "posting_certificate"
];
function isDriverPersonalDocument(type) {
  return DRIVER_PERSONAL_DOCUMENT_TYPES.includes(type);
}
var EXPENSE_KINDS = [
  "fuel",
  "adblue",
  "toll",
  "vignette",
  "ferry",
  "parking",
  "accommodation",
  "per_diem",
  "repair",
  "tyres",
  "wash",
  "fine",
  "customs_fee",
  "subcontractor",
  "other"
];
var INVOICE_STATUSES = [
  "draft",
  "issued",
  "sent",
  "partially_paid",
  "paid",
  "overdue",
  "cancelled",
  "credited"
];
var TRIP_EVENT_TYPES = [
  "created",
  "dispatched",
  "accepted",
  "rejected",
  "started",
  "arrived_pickup",
  "loaded",
  "departed",
  "border_crossed",
  "break_started",
  "rest_started",
  "arrived_delivery",
  "unloaded",
  "pod_captured",
  "completed",
  "delayed",
  "incident",
  "cancelled",
  "note",
  "ecmr_signed"
];
var TACHOGRAPH_GENERATIONS = [
  "none",
  "analogue",
  "digital",
  "smart_v1",
  "smart_v2",
  "unknown"
];
var COMPLIANT_TACHOGRAPH_GENERATIONS = ["smart_v2"];
var CURRENCIES = ["EUR", "BGN", "USD", "CHF", "GBP", "PLN", "RON", "HUF", "CZK"];
var LANGUAGES = ["bg", "en", "de", "it", "ro"];
var EU_MEMBER_STATES = [
  "AT",
  "BE",
  "BG",
  "HR",
  "CY",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SK",
  "SI",
  "ES",
  "SE"
];
function isEuCountry(code) {
  return !!code && EU_MEMBER_STATES.includes(code.toUpperCase());
}
var COMMON_TRANSIT_NON_EU = ["CH", "NO", "IS", "LI", "RS", "MK", "TR", "UA", "GB", "MD"];
function requiresTransitDocument(countries) {
  return countries.some((c) => COMMON_TRANSIT_NON_EU.includes(c.toUpperCase()));
}

// packages/contracts/src/money.ts
import { z } from "zod";
var moneySchema = z.object({
  minor: z.number().int(),
  ccy: z.enum(CURRENCIES)
});
var BGN_PER_EUR_X1E6 = 1955830;
var money = (minor, ccy = "EUR") => ({
  minor: Math.round(minor),
  ccy
});
var zero = (ccy = "EUR") => ({ minor: 0, ccy });
function assertSame(a, b) {
  if (a.ccy !== b.ccy) {
    throw new Error(`\u041D\u0435 \u043C\u043E\u0436\u0435 \u0434\u0430 \u0441\u0435 \u0441\u043C\u044F\u0442\u0430\u0442 ${a.ccy} \u0438 ${b.ccy} \u0431\u0435\u0437 \u0438\u0437\u0440\u0438\u0447\u0435\u043D \u043A\u0443\u0440\u0441`);
  }
}
var add = (a, b) => (assertSame(a, b), money(a.minor + b.minor, a.ccy));
var sub = (a, b) => (assertSame(a, b), money(a.minor - b.minor, a.ccy));
var mul = (a, factor) => money(a.minor * factor, a.ccy);
var neg = (a) => money(-a.minor, a.ccy);
var isZero = (a) => a.minor === 0;
var cmp = (a, b) => (assertSame(a, b), a.minor - b.minor);
var sum = (items, ccy = "EUR") => items.reduce((acc, m) => add(acc, m), zero(ccy));
var share = (a, bps) => money(Math.round(a.minor * bps / 1e4), a.ccy);
function allocate(total, weights) {
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  if (totalWeight <= 0) return weights.map(() => zero(total.ccy));
  const parts = weights.map((w) => Math.floor(total.minor * w / totalWeight));
  let remainder = total.minor - parts.reduce((a, b) => a + b, 0);
  const order = weights.map((w, i) => ({ w, i })).sort((a, b) => b.w - a.w).map((x) => x.i);
  let k = 0;
  while (remainder !== 0 && order.length > 0) {
    const idx = order[k % order.length];
    const step = remainder > 0 ? 1 : -1;
    parts[idx] = (parts[idx] ?? 0) + step;
    remainder -= step;
    k++;
  }
  return parts.map((p) => money(p, total.ccy));
}
function formatMoney(m, locale = "bg-BG") {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: m.ccy,
    minimumFractionDigits: 2
  }).format(m.minor / 100);
}
function parseMoney(input, ccy = "EUR") {
  const cleaned = input.replace(/\s| /g, "").replace(",", ".");
  const value = Number(cleaned);
  if (!Number.isFinite(value)) return null;
  return money(Math.round(value * 100), ccy);
}
function bgnToEur(bgnMinor) {
  return money(Math.round(bgnMinor * 1e6 / BGN_PER_EUR_X1E6), "EUR");
}

// packages/contracts/src/geo.ts
import { z as z2 } from "zod";
var E7 = 1e7;
var latLonSchema = z2.object({
  lat: z2.number().min(-90).max(90),
  lon: z2.number().min(-180).max(180)
});
var toE7 = (deg) => Math.round(deg * E7);
var fromE7 = (e7) => e7 / E7;
var pointFromE7 = (latE7, lonE7) => ({
  lat: fromE7(latE7),
  lon: fromE7(lonE7)
});
var EARTH_RADIUS_M = 63710088e-1;
var rad = (d) => d * Math.PI / 180;
function haversineMeters(a, b) {
  const dLat = rad(b.lat - a.lat);
  const dLon = rad(b.lon - a.lon);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(s)));
}
var ROAD_DETOUR_FACTOR = 1.25;
function estimateRoadKm(a, b) {
  return Math.round(haversineMeters(a, b) / 1e3 * ROAD_DETOUR_FACTOR);
}
function isInsideCircle(point, center, radiusM) {
  return haversineMeters(point, center) <= radiusM;
}
function isInsidePolygon(point, polygon) {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const pi = polygon[i];
    const pj = polygon[j];
    const intersects = pi.lat > point.lat !== pj.lat > point.lat && point.lon < (pj.lon - pi.lon) * (point.lat - pi.lat) / (pj.lat - pi.lat) + pi.lon;
    if (intersects) inside = !inside;
  }
  return inside;
}

// packages/contracts/src/api.ts
import { z as z3 } from "zod";
var uuidSchema = z3.string().uuid();
var isoDateTime = z3.string().datetime({ offset: true });
var queryBoolean = (fallback) => z3.preprocess((value) => {
  if (typeof value !== "string") return value;
  const text = value.trim().toLowerCase();
  if (["true", "1", "yes", "on"].includes(text)) return true;
  if (["false", "0", "no", "off", ""].includes(text)) return false;
  return value;
}, z3.boolean().default(fallback));
var paginationSchema = z3.object({
  page: z3.coerce.number().int().min(1).default(1),
  perPage: z3.coerce.number().int().min(1).max(200).default(50)
});
var inquiryInputSchema = z3.object({
  contactName: z3.string().min(2, "\u0412\u044A\u0432\u0435\u0434\u0435\u0442\u0435 \u0438\u043C\u0435 \u0438 \u0444\u0430\u043C\u0438\u043B\u0438\u044F").max(120),
  contactCompany: z3.string().max(160).optional(),
  contactEmail: z3.string().email("\u041D\u0435\u0432\u0430\u043B\u0438\u0434\u0435\u043D \u0438\u043C\u0435\u0439\u043B \u0430\u0434\u0440\u0435\u0441"),
  contactPhone: z3.string().min(6, "\u0412\u044A\u0432\u0435\u0434\u0435\u0442\u0435 \u0442\u0435\u043B\u0435\u0444\u043E\u043D").max(32),
  contactLang: z3.enum(LANGUAGES).default("bg"),
  fromCountry: z3.string().length(2),
  fromCity: z3.string().min(2).max(120),
  toCountry: z3.string().length(2),
  toCity: z3.string().min(2).max(120),
  readyAt: isoDateTime,
  deadlineAt: isoDateTime.optional(),
  /**
   * Горна граница на запитването. Бусовете са до 2.5 т ОБЩО (бус + товар),
   * така че тежка пратка може да тръгне с повече от един бус — решава офисът.
   */
  weightKg: z3.coerce.number().int().min(1).max(2500),
  lengthCm: z3.coerce.number().int().min(1).max(1200).optional(),
  widthCm: z3.coerce.number().int().min(1).max(300).optional(),
  heightCm: z3.coerce.number().int().min(1).max(300).optional(),
  goodsDescription: z3.string().min(3).max(1e3),
  isAdr: z3.boolean().default(false),
  adrUnNumbers: z3.array(z3.string().regex(/^\d{4}$/)).optional(),
  message: z3.string().max(2e3).optional(),
  /** GDPR — без съгласие няма обработка */
  consent: z3.literal(true, {
    errorMap: () => ({ message: "\u041D\u0435\u043E\u0431\u0445\u043E\u0434\u0438\u043C\u043E \u0435 \u0441\u044A\u0433\u043B\u0430\u0441\u0438\u0435 \u0437\u0430 \u043E\u0431\u0440\u0430\u0431\u043E\u0442\u043A\u0430 \u043D\u0430 \u0434\u0430\u043D\u043D\u0438\u0442\u0435" })
  }),
  /** Скрито поле-капан срещу ботове; ако е попълнено, заявката е спам */
  website: z3.string().max(0).optional()
});
var orderStopInputSchema = z3.object({
  kind: z3.enum(STOP_KINDS),
  placeId: uuidSchema.optional(),
  name: z3.string().min(2).max(160),
  address: z3.string().min(3).max(300),
  city: z3.string().min(2).max(120),
  postcode: z3.string().max(16).optional(),
  country: z3.string().length(2),
  lat: z3.number().min(-90).max(90).optional(),
  lon: z3.number().min(-180).max(180).optional(),
  contactName: z3.string().max(120).optional(),
  contactPhone: z3.string().max(32).optional(),
  windowFrom: isoDateTime.optional(),
  windowTo: isoDateTime.optional(),
  isHardDeadline: z3.boolean().default(false),
  reference: z3.string().max(80).optional(),
  instructions: z3.string().max(1e3).optional()
});
var orderInputSchema = z3.object({
  partnerId: uuidSchema,
  customerRef: z3.string().max(80).optional(),
  quoteId: uuidSchema.optional(),
  agreedPriceMinor: z3.number().int().min(0),
  ccy: z3.enum(CURRENCIES).default("EUR"),
  goodsDescription: z3.string().min(3).max(1e3),
  totalWeightKg: z3.number().int().min(1).max(3500),
  /** Обем в m³ — с теглото решава колко заявки се побират в един бус. */
  volumeM3: z3.number().min(0.01).max(40).optional(),
  packagesCount: z3.number().int().min(1).optional(),
  declaredValueMinor: z3.number().int().min(0).optional(),
  isAdr: z3.boolean().default(false),
  adrPoints: z3.number().int().min(0).optional(),
  requiresTailLift: z3.boolean().default(false),
  /** Планирани километри — срещу тях се мери реалният GPS пробег по курса. */
  plannedDistanceKm: z3.number().int().min(0).max(2e4).optional(),
  notes: z3.string().max(2e3).optional(),
  stops: z3.array(orderStopInputSchema).min(2, "\u041D\u0443\u0436\u043D\u0438 \u0441\u0430 \u043F\u043E\u043D\u0435 \u0442\u043E\u0432\u0430\u0440\u0435\u043D\u0435 \u0438 \u0440\u0430\u0437\u0442\u043E\u0432\u0430\u0440\u0432\u0430\u043D\u0435")
}).refine((o) => o.stops.some((s) => s.kind === "pickup"), {
  message: "\u041B\u0438\u043F\u0441\u0432\u0430 \u0430\u0434\u0440\u0435\u0441 \u0437\u0430 \u0442\u043E\u0432\u0430\u0440\u0435\u043D\u0435",
  path: ["stops"]
}).refine((o) => o.stops.some((s) => s.kind === "delivery"), {
  message: "\u041B\u0438\u043F\u0441\u0432\u0430 \u0430\u0434\u0440\u0435\u0441 \u0437\u0430 \u0440\u0430\u0437\u0442\u043E\u0432\u0430\u0440\u0432\u0430\u043D\u0435",
  path: ["stops"]
});
var driverEventSchema = z3.object({
  clientEventId: z3.string().min(8).max(64),
  tripId: uuidSchema,
  orderStopId: uuidSchema.optional(),
  type: z3.enum(TRIP_EVENT_TYPES),
  at: isoDateTime,
  lat: z3.number().min(-90).max(90).optional(),
  lon: z3.number().min(-180).max(180).optional(),
  accuracyM: z3.number().int().min(0).max(1e4).optional(),
  note: z3.string().max(1e3).optional(),
  payload: z3.record(z3.unknown()).optional()
});
var driverSyncSchema = z3.object({
  events: z3.array(driverEventSchema).max(200),
  /** Последната позната на устройството версия — сървърът връща само промените */
  since: isoDateTime.optional()
});
var documentUploadInitSchema = z3.object({
  clientDocId: z3.string().min(8).max(64),
  type: z3.enum(DOCUMENT_TYPES),
  orderId: uuidSchema.optional(),
  orderStopId: uuidSchema.optional(),
  tripId: uuidSchema.optional(),
  /** Документ на бус — талон, застраховка, винетка. */
  vehicleId: uuidSchema.optional(),
  /** Документ на шофьор — личните се виждат само от управителя. */
  driverId: uuidSchema.optional(),
  notes: z3.string().trim().max(500).optional(),
  /** До кога важи документът (лична карта, медицинско, сертификат). */
  validUntil: z3.string().regex(/^\d{4}-\d{2}-\d{2}$/, "\u0414\u0430\u0442\u0430 \u0432\u044A\u0432 \u0432\u0438\u0434\u0430 \u0413\u0413\u0413\u0413-\u041C\u041C-\u0414\u0414").optional(),
  fileName: z3.string().min(1).max(255),
  mimeType: z3.string().min(3).max(120),
  sizeBytes: z3.number().int().min(1).max(30 * 1024 * 1024),
  sha256: z3.string().regex(/^[a-f0-9]{64}$/),
  capturedAt: isoDateTime.optional(),
  lat: z3.number().optional(),
  lon: z3.number().optional(),
  accuracyM: z3.number().int().optional()
});
var trackingLookupSchema = z3.object({
  /** Нашият номер, номерът на клиента, или публичната референция. */
  reference: z3.string().trim().min(3, "\u0412\u044A\u0432\u0435\u0434\u0435\u0442\u0435 \u043D\u043E\u043C\u0435\u0440 \u043D\u0430 \u0437\u0430\u044F\u0432\u043A\u0430\u0442\u0430").max(80),
  /** Пощенски код на адреса за разтоварване. Не се иска при KMD-референция. */
  postcode: z3.string().trim().max(16).optional()
});

// packages/contracts/src/maintenance.ts
var MECHANIC_CHECK_ITEMS = [
  "tyres",
  "brakes",
  "oil",
  "fluids",
  "lights",
  "suspension",
  "battery",
  "wipers",
  "leaks",
  "body",
  "equipment",
  "documents"
];
var VEHICLE_CHECK_LABELS = {
  tyres: "\u0413\u0443\u043C\u0438 \u2014 \u0433\u0440\u0430\u0439\u0444\u0435\u0440 \u0438 \u043D\u0430\u043B\u044F\u0433\u0430\u043D\u0435",
  lights: "\u0421\u0432\u0435\u0442\u043B\u0438\u043D\u0438 \u0438 \u043C\u0438\u0433\u0430\u0447\u0438",
  brakes: "\u0421\u043F\u0438\u0440\u0430\u0447\u043A\u0438",
  fluids: "\u0422\u0435\u0447\u043D\u043E\u0441\u0442\u0438 \u2014 \u0430\u043D\u0442\u0438\u0444\u0440\u0438\u0437, \u0441\u043F\u0438\u0440\u0430\u0447\u043D\u0430, AdBlue",
  body: "\u041A\u0430\u0440\u043E\u0441\u0435\u0440\u0438\u044F, \u0432\u0440\u0430\u0442\u0438, \u043A\u043B\u044E\u0447\u0430\u043B\u043A\u0438",
  straps: "\u041A\u043E\u043B\u0430\u043D\u0438 \u0438 \u0440\u0435\u043C\u044A\u0446\u0438 \u0437\u0430 \u0443\u043A\u0440\u0435\u043F\u0432\u0430\u043D\u0435",
  documents: "\u0414\u043E\u043A\u0443\u043C\u0435\u043D\u0442\u0438 \u043D\u0430 \u0431\u0443\u0441\u0430 \u0432 \u043A\u0430\u0431\u0438\u043D\u0430\u0442\u0430",
  extinguisher: "\u041F\u043E\u0436\u0430\u0440\u043E\u0433\u0430\u0441\u0438\u0442\u0435\u043B \u0438 \u0430\u043F\u0442\u0435\u0447\u043A\u0430",
  cleanliness: "\u0427\u0438\u0441\u0442 \u0442\u043E\u0432\u0430\u0440\u0435\u043D \u043E\u0442\u0441\u0435\u043A",
  oil: "\u041C\u0430\u0441\u043B\u043E \u0438 \u0444\u0438\u043B\u0442\u0440\u0438",
  suspension: "\u041E\u043A\u0430\u0447\u0432\u0430\u043D\u0435 \u0438 \u043A\u043E\u0440\u043C\u0438\u043B\u043D\u043E",
  battery: "\u0410\u043A\u0443\u043C\u0443\u043B\u0430\u0442\u043E\u0440",
  wipers: "\u0427\u0438\u0441\u0442\u0430\u0447\u043A\u0438 \u0438 \u0441\u0442\u044A\u043A\u043B\u0430",
  leaks: "\u0422\u0435\u0447\u043E\u0432\u0435 \u043F\u043E\u0434 \u0431\u0443\u0441\u0430",
  equipment: "\u041F\u043E\u0436\u0430\u0440\u043E\u0433\u0430\u0441\u0438\u0442\u0435\u043B, \u0430\u043F\u0442\u0435\u0447\u043A\u0430, \u0442\u0440\u0438\u044A\u0433\u044A\u043B\u043D\u0438\u043A"
};
var REPAIR_STATUSES = ["open", "in_progress", "done", "cancelled"];
var REPAIR_STATUS_LABELS = {
  open: "\u0437\u0430 \u043F\u0440\u0430\u0432\u0435\u043D\u0435",
  in_progress: "\u0432 \u0440\u0430\u0431\u043E\u0442\u0430",
  done: "\u0433\u043E\u0442\u043E\u0432\u043E",
  cancelled: "\u043E\u0442\u043A\u0430\u0437\u0430\u043D"
};
var REPAIR_PRIORITIES = ["urgent", "normal", "low"];
var REPAIR_PRIORITY_LABELS = {
  urgent: "\u0441\u043F\u0435\u0448\u043D\u043E \u2014 \u0431\u0443\u0441\u044A\u0442 \u043D\u0435 \u0442\u0440\u044A\u0433\u0432\u0430",
  normal: "\u043D\u043E\u0440\u043C\u0430\u043B\u043D\u043E",
  low: "\u043F\u0440\u0438 \u0441\u043B\u0435\u0434\u0432\u0430\u0449\u043E\u0442\u043E \u043C\u0438\u043D\u0430\u0432\u0430\u043D\u0435"
};
var PART_STATUSES = ["to_buy", "ordered", "bought"];
var PART_STATUS_LABELS = {
  to_buy: "\u0437\u0430 \u043A\u0443\u043F\u0443\u0432\u0430\u043D\u0435",
  ordered: "\u043F\u043E\u0440\u044A\u0447\u0430\u043D\u0430",
  bought: "\u043A\u0443\u043F\u0435\u043D\u0430"
};
export {
  BGN_PER_EUR_X1E6,
  COMMON_TRANSIT_NON_EU,
  COMPLIANT_TACHOGRAPH_GENERATIONS,
  CURRENCIES,
  DOCUMENT_TYPES,
  DRIVER_PERSONAL_DOCUMENT_TYPES,
  E7,
  EU_MEMBER_STATES,
  EXPENSE_KINDS,
  INVOICE_STATUSES,
  LANGUAGES,
  LEG_KINDS,
  MECHANIC_CHECK_ITEMS,
  ORDER_STATUSES,
  PART_STATUSES,
  PART_STATUS_LABELS,
  REPAIR_PRIORITIES,
  REPAIR_PRIORITY_LABELS,
  REPAIR_STATUSES,
  REPAIR_STATUS_LABELS,
  ROAD_DETOUR_FACTOR,
  STOP_KINDS,
  STOP_STATUSES,
  TACHOGRAPH_GENERATIONS,
  TRIP_EVENT_TYPES,
  TRIP_STATUSES,
  USER_ROLES,
  VEHICLE_CHECK_LABELS,
  add,
  allocate,
  bgnToEur,
  cmp,
  documentUploadInitSchema,
  driverEventSchema,
  driverSyncSchema,
  estimateRoadKm,
  formatMoney,
  fromE7,
  haversineMeters,
  inquiryInputSchema,
  isDriverPersonalDocument,
  isEuCountry,
  isInsideCircle,
  isInsidePolygon,
  isZero,
  isoDateTime,
  latLonSchema,
  money,
  moneySchema,
  mul,
  neg,
  orderInputSchema,
  orderStopInputSchema,
  paginationSchema,
  parseMoney,
  pointFromE7,
  queryBoolean,
  requiresTransitDocument,
  share,
  sub,
  sum,
  toE7,
  trackingLookupSchema,
  uuidSchema,
  zero
};
//# sourceMappingURL=index.js.map
